import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAccount, useWriteContract, usePublicClient } from "wagmi";
import { useAppKit } from "@reown/appkit/react";
import { toast } from "sonner";
import { Rocket, X, Lock, Coins, ImageIcon, Info, Upload, Loader2, Link2 } from "lucide-react";
import { parseUnits, decodeEventLog } from "viem";
import { getPairs, createCoin, uploadImage } from "@/lib/api";
import { FACTORY_ABI, BASE_CHAIN_ID } from "@/config/contracts";
import { useConfig } from "@/hooks/useConfig";
import { computeInitialTick } from "@/lib/tick";

const CATEGORIES = ["Memecoins", "Large Cap", "Stocks & Gold", "Buyback & Burn", "Base Ecosystem"];

export default function LaunchModal({ open, onClose }) {
  const { data: pairs = [] } = useQuery({ queryKey: ["pairs"], queryFn: getPairs });
  const { address, isConnected } = useAccount();
  const { open: openWallet } = useAppKit();
  const { writeContractAsync } = useWriteContract();
  const publicClient = usePublicClient();
  const { factory } = useConfig();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "", symbol: "", description: "", image: "",
    pair_symbol: "ETH", category: "Memecoins", initial_buy: "",
    website: "", x_account: "", tweet: "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  if (!open) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pair = pairs.find((p) => p.symbol === form.pair_symbol);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await uploadImage(fd);
      setForm((f) => ({ ...f, image: res.url }));
      toast.success("Image pinned to IPFS");
    } catch (err) {
      toast.error("Image upload failed", { description: err?.response?.data?.detail || err?.message });
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.name || !form.symbol) return toast.error("Name and symbol are required");
    if (!isConnected) {
      toast.message("Connect your wallet to launch");
      return openWallet();
    }
    setBusy(true);
    let tx_hash = "";
    let token_address = "";
    let pool_id = "";
    try {
      if (factory) {
        // Real on-chain launch: deploys the token, opens its Uniswap v4 market, seeds
        // single-sided liquidity and executes the creator's first buy in one transaction
        // via the BluePadFactory. Links are stored on-chain. Opening tick targets $2,000.
        const initialTick = computeInitialTick(pair?.symbol, pair?.decimals);
        const buyRaw = form.initial_buy ? parseUnits(String(form.initial_buy), pair?.decimals ?? 18) : 0n;
        const value = form.pair_symbol === "ETH" ? buyRaw : 0n;
        tx_hash = await writeContractAsync({
          address: factory,
          abi: FACTORY_ABI,
          functionName: "launch",
          args: [
            form.name, form.symbol, pair?.address, initialTick,
            form.website || "", form.x_account || "", form.tweet || "", buyRaw,
          ],
          value,
          chainId: BASE_CHAIN_ID,
        });
        toast.success("Launch transaction submitted", { description: "Your first buy executes before anyone else can trade." });
        // Wait for the receipt and decode the Launched event to capture the token address (CA).
        try {
          const receipt = await publicClient.waitForTransactionReceipt({ hash: tx_hash });
          for (const log of receipt.logs) {
            try {
              const ev = decodeEventLog({ abi: FACTORY_ABI, data: log.data, topics: log.topics });
              if (ev.eventName === "Launched") {
                token_address = ev.args.token;
                pool_id = ev.args.poolId;
                break;
              }
            } catch (_) {}
          }
        } catch (_) {}
      } else {
        toast.message("On-chain factory not deployed yet — recording your launch", {
          description: "The Uniswap v4 pool will be created once the factory contract is live on Base.",
        });
      }
      const coin = await createCoin({
        ...form,
        initial_buy: parseFloat(form.initial_buy) || 0,
        creator: address || "",
        tx_hash,
        token_address,
        pool_id,
      });
      qc.invalidateQueries({ queryKey: ["coins"] });
      toast.success(`$${coin.symbol} launched`, { description: `Paired with ${coin.pair_symbol}` });
      onClose();
      navigate(`/coin/${coin.id}`);
    } catch (e) {
      toast.error("Launch failed", { description: e?.shortMessage || e?.message || "Try again" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" data-testid="launch-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="animate-rise relative z-10 max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-white/10 bg-[#0d111a] p-6 sm:rounded-3xl">
        <div className="mb-5 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 font-head text-xl font-extrabold">
              <Rocket size={20} className="text-[#00f0ff]" /> Launch a coin
            </div>
            <p className="mt-1 text-sm text-slate-400">
              One transaction opens your coin's Uniswap v4 market. Liquidity locked forever.
            </p>
          </div>
          <button onClick={onClose} data-testid="launch-modal-close" className="rounded-lg p-1.5 text-slate-400 hover:bg-white/5 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Coin name">
              <input data-testid="launch-name-input" value={form.name} onChange={set("name")} placeholder="Base Yak" className={inputCls} />
            </Field>
            <Field label="Ticker">
              <input data-testid="launch-symbol-input" value={form.symbol} onChange={(e) => setForm((f) => ({ ...f, symbol: e.target.value.toUpperCase().slice(0, 10) }))} placeholder="BYAK" className={`${inputCls} font-mono2`} />
            </Field>
          </div>

          <Field label="Description">
            <textarea data-testid="launch-desc-input" value={form.description} onChange={set("description")} rows={2} placeholder="What is your coin about?" className={`${inputCls} resize-none`} />
          </Field>

          <Field label="Links (stored on-chain)" icon={<Link2 size={13} />}>
            <div className="space-y-2">
              <input data-testid="launch-website-input" value={form.website} onChange={set("website")} placeholder="Website — https://yoursite.xyz" className={inputCls} />
              <input data-testid="launch-xaccount-input" value={form.x_account} onChange={set("x_account")} placeholder="X account — https://x.com/yourhandle" className={inputCls} />
              <input data-testid="launch-tweet-input" value={form.tweet} onChange={set("tweet")} placeholder="Tweet — https://x.com/…/status/…" className={inputCls} />
            </div>
          </Field>

          <Field label="Image" icon={<ImageIcon size={13} />}>
            <div className="flex gap-2">
              <input data-testid="launch-image-input" value={form.image} onChange={set("image")} placeholder="Paste image URL or upload to IPFS" className={inputCls} />
              <label className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-xl border border-white/10 bg-[#131824] px-3 text-xs font-semibold text-slate-200 hover:border-blue-500/50">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                IPFS
                <input data-testid="launch-image-upload" type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} />
              </label>
            </div>
            {form.image && <img src={form.image} alt="preview" className="mt-2 h-14 w-14 rounded-lg border border-white/10 object-cover" />}
          </Field>

          <Field label="Paired asset">
            <div className="grid grid-cols-5 gap-2" data-testid="paired-select-dropdown">
              {pairs.map((p) => (
                <button
                  key={p.symbol}
                  onClick={() => setForm((f) => ({ ...f, pair_symbol: p.symbol }))}
                  data-testid={`pair-option-${p.symbol}`}
                  className={`flex flex-col items-center gap-1 rounded-xl border p-2 transition-all ${
                    form.pair_symbol === p.symbol
                      ? "border-blue-500 bg-blue-500/10"
                      : "border-white/10 bg-[#131824] hover:border-white/25"
                  }`}
                >
                  <img src={p.logo} alt={p.symbol} className="h-6 w-6 rounded-full" onError={(e) => (e.currentTarget.style.visibility = "hidden")} />
                  <span className="font-mono2 text-[10px] font-semibold">{p.symbol}</span>
                </button>
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select data-testid="launch-category-select" value={form.category} onChange={set("category")} className={inputCls}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={`First buy (${form.pair_symbol})`} icon={<Coins size={13} />}>
              <input data-testid="launch-initialbuy-input" value={form.initial_buy} onChange={set("initial_buy")} type="number" min="0" step="any" placeholder="0.0" className={`${inputCls} font-mono2`} />
            </Field>
          </div>
          <p className="-mt-1 text-[11px] text-slate-500">Your first buy executes in the same launch transaction, before anyone else can trade.{form.pair_symbol !== "ETH" && form.initial_buy ? " For ERC20 pairs, approve the factory first." : ""}</p>

          <div className="flex items-start gap-2 rounded-xl border border-[#00e699]/20 bg-[#00e699]/5 p-3 text-xs text-slate-300">
            <Lock size={14} className="mt-0.5 shrink-0 text-[#00e699]" />
            <span>Every swap carries a <b className="text-[#00e699]">2% tax paid in {form.pair_symbol}</b> — 60% to you as creator, 40% to the platform. Liquidity is locked forever.</span>
          </div>

          {!factory && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-amber-200/80">
              <Info size={14} className="mt-0.5 shrink-0 text-amber-400" />
              <span>Factory contract not deployed on Base yet. Your launch is recorded now and goes fully on-chain once the factory is live.</span>
            </div>
          )}

          <button data-testid="launch-modal-submit" onClick={submit} disabled={busy} className="btn-accent flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold text-white disabled:opacity-60">
            <Rocket size={16} /> {busy ? "Launching…" : `Launch $${form.symbol || "COIN"} · paired with ${form.pair_symbol}`}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#131824] px-3 py-2.5 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-blue-500/60";

function Field({ label, icon, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-400">{icon}{label}</span>
      {children}
    </label>
  );
}
